import { motion } from "framer-motion";

export const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.2 }}
    variants={{
      hidden: { opacity: 0, y: 18 },
      show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1], delay } },
    }}
  >
    {children}
  </motion.div>
);

export const Container = ({ children, className = "", wide = false }) => (
  <div className={`mx-auto ${wide ? "max-w-7xl" : "max-w-6xl"} px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

export const Section = ({ children, className = "", id, dense = false }) => (
  <section id={id} className={`${dense ? "py-12 sm:py-16" : "py-16 sm:py-20 lg:py-28"} ${className}`}>
    {children}
  </section>
);

export const Eyebrow = ({ children, onNavy = false }) => (
  <div className="flex items-center gap-3">
    <span className="h-px w-8" style={{ background: "var(--pt-gold)" }} />
    <span
      className="font-sans text-xs sm:text-[13px] font-semibold uppercase tracking-[0.22em]"
      style={{ color: onNavy ? "var(--pt-gold-soft)" : "var(--pt-gold)" }}
    >
      {children}
    </span>
  </div>
);

export const SectionHeading = ({ eyebrow, title, intro, onNavy = false, center = false, className = "" }) => (
  <div className={`${center ? "mx-auto text-center items-center" : ""} flex flex-col ${center ? "" : "items-start"} max-w-3xl ${className}`}>
    {eyebrow && (
      <div className={center ? "flex justify-center w-full mb-4" : "mb-4"}>
        <Eyebrow onNavy={onNavy}>{eyebrow}</Eyebrow>
      </div>
    )}
    <h2
      className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.12] tracking-[-0.015em]"
      style={{ color: onNavy ? "#FFFFFF" : "var(--pt-navy)" }}
    >
      {title}
    </h2>
    {intro && (
      <p
        className="mt-5 font-sans text-base sm:text-lg leading-relaxed"
        style={{ color: onNavy ? "var(--pt-muted-on-navy)" : "var(--pt-muted)" }}
      >
        {intro}
      </p>
    )}
  </div>
);

// Subtle leaf/wreath inspired decorative SVG backdrop
export const LeafBackdrop = ({ className = "", color = "rgba(58,187,176,0.10)", size = 460 }) => (
  <svg
    className={`pointer-events-none absolute ${className}`}
    width={size}
    height={size}
    viewBox="0 0 200 200"
    fill="none"
    aria-hidden="true"
  >
    <g stroke={color} strokeWidth="1.4" fill="none">
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 12;
        const cx = 100 + Math.cos(a) * 62;
        const cy = 100 + Math.sin(a) * 62;
        const rot = (a * 180) / Math.PI + 90;
        return (
          <g key={i} transform={`translate(${cx} ${cy}) rotate(${rot})`}>
            <path d="M0,-13 C7,-7 7,7 0,13 C-7,7 -7,-7 0,-13 Z" />
            <circle cx="0" cy="18" r="2.4" fill={color} stroke="none" />
          </g>
        );
      })}
      <circle cx="100" cy="100" r="40" strokeDasharray="2 6" />
    </g>
  </svg>
);

export const GoldDivider = ({ className = "" }) => (
  <div className={`gold-hairline ${className}`} />
);

export const ImageFrame = ({ src, alt, className = "", imgClassName = "" }) => (
  <div className={`relative overflow-hidden rounded-[var(--radius)] ${className}`} style={{ boxShadow: "var(--shadow-card)" }}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.04] ${imgClassName}`}
    />
    <div className="absolute inset-0 ring-1 ring-inset" style={{ borderRadius: "var(--radius)", boxShadow: "inset 0 0 0 1px rgba(58,187,176,0.30)" }} />
  </div>
);
